import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Chip, Grid, Stack, Typography, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { usePortfolioClimateData, type PropertyResult } from '../../../hooks/climateData'
import { useLocations } from '../../../hooks/locations'
import siteMap from '../../../siteMap'
import { RootState } from '../../../store'
import { CanonicalPeriod, EmissionTier, MetricState, PERIODS } from '../common'
import { renderAggregateTooltip } from '../common/aggregateHelpText'
import { parseMetricPointY } from '../common/financialMetrics'
import { FreezeThawScoreBadge, freezeThawScoreColors, freezeThawScoreLabels } from '../common/freezeThaw/utils'
import { meanAggregateValues } from '../common/portfolioAggregates'
import { LoadingValue } from './PortfolioLoading'
import PortfolioPropertyBreakdownTable, { type PortfolioMetricSpec } from './PortfolioPropertyBreakdownTable'
import PortfolioPropertyMetricMap, {
  type NumericMetricSpec,
  type PopupFieldSpec,
  type PortfolioMapColorOption,
  type PortfolioMapRadiusOption,
} from './PortfolioPropertyMetricMap'
import PortfolioSummaryMetricCard from './PortfolioSummaryMetricCard'

type PropertySubset = { mode: 'all' } | { mode: 'selected'; ids: number[] }

type PeriodicMetricMap = {
  values: Record<number, Partial<Record<CanonicalPeriod, number | null>>>
  status: Record<number, MetricState | null>
}

function parsePoint(entry: PropertyResult, tier: EmissionTier, period: CanonicalPeriod): number | null {
  if (entry.state?.value !== MetricState.SUCCEEDED) return null
  return parseMetricPointY(entry.data?.data?.[tier]?.[period])
}

function buildMetricMap(source: Record<number, PropertyResult> | undefined, tier: EmissionTier): PeriodicMetricMap {
  const values: PeriodicMetricMap['values'] = {}
  const status: PeriodicMetricMap['status'] = {}

  if (source) {
    Object.values(source).forEach(entry => {
      const propertyId = entry?.property_id
      if (typeof propertyId !== 'number') return

      status[propertyId] = entry.state?.value ?? null
      values[propertyId] = Object.fromEntries(
        PERIODS.map(period => [period, parsePoint(entry, tier, period)]),
      ) as Partial<Record<CanonicalPeriod, number | null>>
    })
  }

  return { values, status }
}

function formatDays(value: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)
}

type PortfolioFreezeThawProps = {
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
}

export default function PortfolioFreezeThaw({ emissionTier, selectedPeriod }: PortfolioFreezeThawProps) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations } = useLocations({ group_id: groupId ?? undefined })
  const { data: scoresByProperty, isLoading: scoresLoading } = usePortfolioClimateData('freeze_thaw:score', {
    groupId: groupId ?? undefined,
  })
  const { data: daysByProperty, isLoading: daysLoading } = usePortfolioClimateData('freeze_thaw:freeze_thaw_days', {
    groupId: groupId ?? undefined,
  })

  const [propertySubset, setPropertySubset] = useState<PropertySubset>({ mode: 'all' })
  const [mapClearSelectionToken, setMapClearSelectionToken] = useState(0)

  useEffect(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(value => value + 1)
  }, [groupId])

  const activePropertyIds = propertySubset.mode === 'selected' ? propertySubset.ids : undefined
  const selectedPropertyIds = useMemo(
    () => activePropertyIds ?? (locations ?? []).map((location: { id: number }) => location.id),
    [activePropertyIds, locations],
  )
  const scoreMetric = useMemo(() => buildMetricMap(scoresByProperty, emissionTier), [emissionTier, scoresByProperty])
  const daysMetric = useMemo(() => buildMetricMap(daysByProperty, emissionTier), [daysByProperty, emissionTier])

  const scoreSummary = useMemo(
    () =>
      meanAggregateValues(selectedPropertyIds, propertyId => scoreMetric.values[propertyId]?.[selectedPeriod] ?? null),
    [scoreMetric.values, selectedPeriod, selectedPropertyIds],
  )
  const daysSummary = useMemo(
    () =>
      meanAggregateValues(selectedPropertyIds, propertyId => daysMetric.values[propertyId]?.[selectedPeriod] ?? null),
    [daysMetric.values, selectedPeriod, selectedPropertyIds],
  )

  const goToProperty = useCallback(
    (propertyId: number) =>
      history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'freeze_thaw', emissionTier, selectedPeriod)),
    [emissionTier, history, selectedPeriod],
  )
  const clearSubset = useCallback(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(value => value + 1)
  }, [])
  const handleConfirmSelection = useCallback((propertyIds: number[]) => {
    setPropertySubset(propertyIds.length ? { mode: 'selected', ids: propertyIds } : { mode: 'all' })
  }, [])

  const tableMetrics = useMemo<PortfolioMetricSpec[]>(
    () => [
      {
        kind: 'score',
        key: 'freeze_thaw_score',
        label: 'Risk Rating',
        perPeriod: true,
        width: 150,
        loading: scoresLoading,
        getValue: (propertyId, period) => scoreMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        renderScore: value => <FreezeThawScoreBadge score={value} size="dense" />,
        status: {
          key: 'freezeThawScoreStatus',
          label: 'Status (Risk Rating)',
          hiddenByDefault: true,
          loading: scoresLoading,
          getValue: propertyId => scoreMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'number',
        key: 'freeze_thaw_days',
        label: 'Freeze-Thaw Days',
        perPeriod: true,
        width: 170,
        loading: daysLoading,
        format: 'fixed1',
        suffix: 'days/year',
        getValue: (propertyId, period) => daysMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'freezeThawDaysStatus',
          label: 'Status (Freeze-Thaw Days)',
          hiddenByDefault: true,
          loading: daysLoading,
          getValue: propertyId => daysMetric.status[propertyId] ?? null,
        },
      },
    ],
    [daysLoading, daysMetric.status, daysMetric.values, scoreMetric.status, scoreMetric.values, scoresLoading],
  )

  const scoreMapMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'freeze_thaw_score',
      label: 'Risk Rating',
      perPeriod: true,
      loading: scoresLoading,
      getValue: (propertyId, period) => scoreMetric.values[propertyId]?.[period] ?? null,
      renderValue: value => <FreezeThawScoreBadge score={value} size="dense" />,
    }),
    [scoreMetric.values, scoresLoading],
  )
  const daysMapMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'freeze_thaw_days',
      label: 'Freeze-Thaw Days',
      perPeriod: true,
      loading: daysLoading,
      decimals: 1,
      suffix: 'days/year',
      getValue: (propertyId, period) => daysMetric.values[propertyId]?.[period] ?? null,
    }),
    [daysLoading, daysMetric.values],
  )

  const mapColorOptions = useMemo<PortfolioMapColorOption[]>(
    () => [
      {
        key: 'freeze_thaw_score',
        label: 'Risk Rating',
        type: 'discrete',
        metric: scoreMapMetric,
        palette: freezeThawScoreColors,
        labels: freezeThawScoreLabels,
        normalize: value => Math.min(5, Math.max(1, Math.round(value))),
      },
      {
        key: 'freeze_thaw_days',
        label: 'Freeze-Thaw Days',
        type: 'continuous',
        metric: daysMapMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#e0f2fe' },
            { offset: 0.5, color: '#38bdf8' },
            { offset: 1, color: '#075985' },
          ],
        },
      },
    ],
    [daysMapMetric, scoreMapMetric],
  )
  const mapRadiusOptions = useMemo<PortfolioMapRadiusOption[]>(
    () => [
      {
        key: 'freeze_thaw_days',
        label: 'Freeze-Thaw Days',
        type: 'scaled',
        metric: daysMapMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
    ],
    [daysMapMetric],
  )
  const popupFields = useMemo<PopupFieldSpec[]>(
    () => [
      {
        key: 'freeze_thaw_score',
        label: 'Risk Rating',
        perPeriod: true,
        loading: scoresLoading,
        getValue: (propertyId, period) => scoreMetric.values[propertyId]?.[period] ?? null,
        renderValue: value => <FreezeThawScoreBadge score={typeof value === 'number' ? value : null} size="dense" />,
      },
      {
        key: 'freeze_thaw_days',
        label: 'Freeze-Thaw Days',
        perPeriod: true,
        loading: daysLoading,
        decimals: 1,
        suffix: 'days/year',
        getValue: (propertyId, period) => daysMetric.values[propertyId]?.[period] ?? null,
      },
    ],
    [daysLoading, daysMetric.values, scoreMetric.values, scoresLoading],
  )

  const transitionKey = `${emissionTier}-${selectedPeriod}`

  return (
    <Stack alignItems="center" pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values.lg, width: '100%' }} spacing={2}>
        {activePropertyIds && (
          <Alert
            severity="info"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={clearSubset}>
                Clear selection
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={800}>
              Property filter
            </Typography>
            <Typography variant="body2">
              Showing data for {activePropertyIds.length.toLocaleString()} of{' '}
              {(locations?.length ?? 0).toLocaleString()} properties.
            </Typography>
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <PortfolioSummaryMetricCard
              title="Average Freeze-Thaw Risk Rating"
              helpText={renderAggregateTooltip(
                scoreSummary,
                'Average Freeze-Thaw Risk Rating',
                'Average across available property ratings',
              )}
              loading={scoresLoading}
              transitionKey={transitionKey}
            >
              <LoadingValue loading={scoresLoading} variant="rounded" width={96} height={24}>
                <FreezeThawScoreBadge score={scoreSummary.value} size="dense" />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <PortfolioSummaryMetricCard
              title="Average Freeze-Thaw Days"
              helpText={renderAggregateTooltip(
                daysSummary,
                'Average Freeze-Thaw Days',
                'Average across available annual property values',
              )}
              loading={daysLoading}
              transitionKey={transitionKey}
            >
              <LoadingValue loading={daysLoading} variant="rounded" width={120} height={24}>
                <Chip label={daysSummary.value == null ? '—' : `${formatDays(daysSummary.value)} days/year`} />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>
        </Grid>

        <PortfolioPropertyMetricMap
          title="Freeze-Thaw Exposure Map"
          height={600}
          selectedPeriod={selectedPeriod}
          colorOptions={mapColorOptions}
          radiusOptions={mapRadiusOptions}
          defaultColorKey="freeze_thaw_score"
          defaultRadiusKey="default"
          popupFields={popupFields}
          onViewProperty={goToProperty}
          onConfirmSelection={handleConfirmSelection}
          onClearSelection={() => setPropertySubset({ mode: 'all' })}
          clearSelectionToken={mapClearSelectionToken}
        />

        <PortfolioPropertyBreakdownTable
          metrics={tableMetrics}
          selectedPeriod={selectedPeriod}
          filterPropertyIds={activePropertyIds}
          onViewProperty={goToProperty}
          height={680}
          initialSort={{ metricKey: 'freeze_thaw_score', period: selectedPeriod, sort: 'desc' }}
        />
      </Stack>
    </Stack>
  )
}
